/** Llamadas HTTP del feature subsidios FNDR. */
import { apiDownload, apiRequest } from "../../lib/api";

export type EstadoAplicacion = "Aplicado" | "Cobrado_FNDR" | "Rechazado_FNDR";

export interface SubsidioRead {
  id: number;
  empresa_id: number;
  contrato_id: number;
  numero_decreto: string;
  fecha_decreto: string | null;
  fecha_inicio: string;
  fecha_vencimiento: string;
  porcentaje_subsidio: number;
  tope_m3: number;
  tope_monetario: number | null;
  activo: boolean;
  organismo_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface SubsidioAplicadoRead {
  id: number;
  empresa_id: number;
  subsidio_id: number;
  lectura_id: number;
  mes: number;
  anio: number;
  m3_subsidiados: number;
  tope_m3_periodo: number;
  monto_aplicado: number;
  monto_recuperado_fndr: number;
  estado: EstadoAplicacion;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubsidioCreatePayload {
  contrato_id: number;
  numero_decreto: string;
  fecha_decreto?: string | null;
  fecha_inicio: string;
  fecha_vencimiento: string;
  porcentaje_subsidio: number;
  tope_m3?: number;
  tope_monetario?: number | null;
  activo?: boolean;
  organismo_id?: number | null;
}

export interface SubsidioUpdatePayload {
  fecha_vencimiento?: string;
  porcentaje_subsidio?: number;
  tope_m3?: number;
  tope_monetario?: number | null;
  activo?: boolean;
  organismo_id?: number | null;
}

export function listarPorContrato(contratoId: number): Promise<SubsidioRead[]> {
  return apiRequest<SubsidioRead[]>(`/api/v1/subsidios/contrato/${contratoId}`);
}

export function obtenerVigente(
  contratoId: number,
): Promise<SubsidioRead | null> {
  return apiRequest<SubsidioRead | null>(
    `/api/v1/subsidios/contrato/${contratoId}/vigente`,
  );
}

export function listarAplicadosPeriodo(
  mes: number,
  anio: number,
): Promise<SubsidioAplicadoRead[]> {
  return apiRequest<SubsidioAplicadoRead[]>(
    `/api/v1/subsidios/aplicados?mes=${mes}&anio=${anio}`,
  );
}

export function crearSubsidio(p: SubsidioCreatePayload): Promise<SubsidioRead> {
  return apiRequest<SubsidioRead>("/api/v1/subsidios", {
    method: "POST",
    body: p,
  });
}

export function actualizarSubsidio(
  id: number,
  p: SubsidioUpdatePayload,
): Promise<SubsidioRead> {
  return apiRequest<SubsidioRead>(`/api/v1/subsidios/${id}`, {
    method: "PATCH",
    body: p,
  });
}

// ──────────────────────────────────────────────────────────────────────
// Organismos subsidiadores (catálogo por tenant)
// ──────────────────────────────────────────────────────────────────────

export interface OrganismoRead {
  id: number;
  empresa_id: number;
  codigo: string;
  nombre: string;
  rut: string | null;
  email: string | null;
  contacto: string | null;
  observaciones: string | null;
  activo: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganismoCreatePayload {
  codigo: string;
  nombre: string;
  rut?: string | null;
  email?: string | null;
  contacto?: string | null;
  observaciones?: string | null;
}

export interface OrganismoUpdatePayload {
  nombre?: string;
  rut?: string | null;
  email?: string | null;
  contacto?: string | null;
  observaciones?: string | null;
  activo?: boolean;
}

export function listarOrganismos(
  incluirInactivos = false,
): Promise<OrganismoRead[]> {
  const q = incluirInactivos ? "?incluir_inactivos=true" : "";
  return apiRequest<OrganismoRead[]>(`/api/v1/subsidios/organismos${q}`);
}

export function crearOrganismo(
  p: OrganismoCreatePayload,
): Promise<OrganismoRead> {
  return apiRequest<OrganismoRead>("/api/v1/subsidios/organismos", {
    method: "POST",
    body: p,
  });
}

export function actualizarOrganismo(
  id: number,
  p: OrganismoUpdatePayload,
): Promise<OrganismoRead> {
  return apiRequest<OrganismoRead>(`/api/v1/subsidios/organismos/${id}`, {
    method: "PATCH",
    body: p,
  });
}

// ──────────────────────────────────────────────────────────────────────
// Rendición mensual al organismo
// ──────────────────────────────────────────────────────────────────────

export interface RendicionLinea {
  aplicado_id: number;
  socio_id: number;
  socio_nombre: string;
  socio_rut: string;
  arranque_codigo: string | null;
  arranque_direccion: string | null;
  sector_nombre: string | null;
  decreto: string;
  fecha_decreto: string | null;
  porcentaje_subsidio: number;
  tope_m3: number;
  m3_subsidiados: number;
  monto_aplicado: number;
  monto_recuperado_fndr: number;
  estado: EstadoAplicacion;
  observaciones: string | null;
  lectura_id: number;
}

export interface RendicionTotales {
  mes: number;
  anio: number;
  organismo_id: number | null;
  organismo_codigo: string | null;
  organismo_nombre: string | null;
  cantidad_aplicaciones: number;
  cantidad_aplicados: number;
  cantidad_cobrados: number;
  cantidad_rechazados: number;
  total_m3_subsidiados: number;
  total_monto_aplicado: number;
  total_monto_recuperado_fndr: number;
  total_monto_pendiente: number;
}

export interface RendicionResponse {
  totales: RendicionTotales;
  lineas: RendicionLinea[];
}

export function fetchRendicion(
  mes: number,
  anio: number,
  organismoId: number | null,
): Promise<RendicionResponse> {
  const params = new URLSearchParams({ mes: String(mes), anio: String(anio) });
  if (organismoId !== null) params.set("organismo_id", String(organismoId));
  return apiRequest<RendicionResponse>(
    `/api/v1/subsidios/rendicion?${params.toString()}`,
  );
}

export function descargarRendicionCsv(
  mes: number,
  anio: number,
  organismoId: number | null,
): Promise<void> {
  const params = new URLSearchParams({ mes: String(mes), anio: String(anio) });
  if (organismoId !== null) params.set("organismo_id", String(organismoId));
  const fallback = `rendicion_${anio}_${String(mes).padStart(2, "0")}.csv`;
  return apiDownload(
    `/api/v1/subsidios/rendicion/export?${params.toString()}`,
    fallback,
  );
}

// ──────────────────────────────────────────────────────────────────────
// Asignar organismo a un Subsidio existente (retroactivo)
// ──────────────────────────────────────────────────────────────────────

export function asignarOrganismoASubsidio(
  subsidioId: number,
  organismoId: number | null,
): Promise<SubsidioRead> {
  return apiRequest<SubsidioRead>(`/api/v1/subsidios/${subsidioId}`, {
    method: "PATCH",
    body: { organismo_id: organismoId },
  });
}
